/**
 * Pure H OS · Sprint 6 (integration) · Global Search page.
 *
 * Full-page mirror of the Command Palette. Results are grouped by kind
 * — Products, Campaigns, Batches, Suppliers, Customers, Invoices,
 * Payments, Creators, Pages, Departments, People, AI, Reports,
 * Approvals — so the platform feels like ONE operating system.
 *
 * Composes only frozen primitives (Card, SectionHeader, TabBar,
 * TextInput, ListRow, EmptyState, Kbd). No new visual language.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, LayoutDashboard, Users, BarChart3, FileText,
  User as UserIcon, Bot, Compass, ArrowRight, ClipboardCheck,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Card, SectionHeader, TextInput, EmptyState } from "../components/lib";
import { TabBar, ListRow, Kbd } from "../components/lib.overlay";
import { SEARCH_INDEX, SearchScope } from "../mocks/platformData";
import { ENTITY_INDEX, ENTITY_KIND_META, EntityKind } from "../mocks/entityLinks";

type ResultKind = EntityKind | SearchScope;

// Unified meta for both entity kinds and platform scopes.
const KIND_META: Record<ResultKind, { label: string; icon: LucideIcon; order: number }> = {
  product:    { label: ENTITY_KIND_META.product.label,    icon: ENTITY_KIND_META.product.icon,    order: 10 },
  campaign:   { label: ENTITY_KIND_META.campaign.label,   icon: ENTITY_KIND_META.campaign.icon,   order: 20 },
  batch:      { label: ENTITY_KIND_META.batch.label,      icon: ENTITY_KIND_META.batch.icon,      order: 30 },
  supplier:   { label: ENTITY_KIND_META.supplier.label,   icon: ENTITY_KIND_META.supplier.icon,   order: 40 },
  customer:   { label: ENTITY_KIND_META.customer.label,   icon: ENTITY_KIND_META.customer.icon,   order: 50 },
  invoice:    { label: ENTITY_KIND_META.invoice.label,    icon: ENTITY_KIND_META.invoice.icon,    order: 60 },
  payment:    { label: ENTITY_KIND_META.payment.label,    icon: ENTITY_KIND_META.payment.icon,    order: 70 },
  creator:    { label: ENTITY_KIND_META.creator.label,    icon: ENTITY_KIND_META.creator.icon,    order: 80 },
  page:       { label: "Pages",       icon: LayoutDashboard, order: 90 },
  department: { label: "Departments", icon: Users,           order: 95 },
  report:     { label: "Reports",     icon: BarChart3,       order: 96 },
  approval:   { label: "Approvals",   icon: ClipboardCheck,  order: 97 },
  person:     { label: "People",      icon: UserIcon,        order: 98 },
  ai:         { label: "AI",          icon: Bot,             order: 99 },
};

const KIND_ORDER = (Object.keys(KIND_META) as ResultKind[])
  .sort((a, b) => KIND_META[a].order - KIND_META[b].order);

type UnifiedRow = {
  id: string;
  kind: ResultKind;
  title: string;
  subtitle: string;
  href: string;
  keywords: string[];
};

function scoreMatch(q: string, hay: string): number {
  const s = hay.toLowerCase();
  const query = q.toLowerCase().trim();
  if (!query) return 1;
  if (s === query) return 100;
  if (s.startsWith(query)) return 80;
  if (s.includes(query)) return 50;
  if (s.split(/\s+/).some((w) => w.startsWith(query))) return 60;
  return 0;
}

// Unified corpus used by both the tabbar counts and the grouped list.
// Computed lazily inside the component so it observes the live
// `ENTITY_INDEX` and `SEARCH_INDEX` proxies rather than a stale
// module-load snapshot.
type CorpusRow = UnifiedRow;
function buildCorpus(): CorpusRow[] {
  return [
    ...ENTITY_INDEX.map<UnifiedRow>((e) => ({
      id: e.id,
      kind: e.kind,
      title: e.title,
      subtitle: e.subtitle,
      href: e.href,
      keywords: e.keywords,
    })),
    ...SEARCH_INDEX.map<UnifiedRow>((s) => ({
      id: s.id,
      kind: s.scope,
      title: s.title,
      subtitle: s.subtitle,
      href: s.href,
      keywords: s.keywords,
    })),
  ];
}

export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [scope, setScope] = useState<ResultKind | "all">("all");
  const navigate = useNavigate();

  const CORPUS = useMemo<CorpusRow[]>(() => buildCorpus(), []);

  const filtered = useMemo(() => {
    return CORPUS
      .map((r) => ({
        r,
        score: Math.max(
          scoreMatch(q, r.title),
          scoreMatch(q, r.subtitle) - 20,
          ...r.keywords.map((k) => scoreMatch(q, k) - 10),
        ),
      }))
      .filter(({ r, score }) => (scope === "all" || r.kind === scope) && score > 0)
      .sort((a, b) => b.score - a.score)
      .map(({ r }) => r);
  }, [q, scope, CORPUS]);

  const grouped = useMemo(() => {
    const g = new Map<ResultKind, UnifiedRow[]>();
    filtered.forEach((r) => {
      if (!g.has(r.kind)) g.set(r.kind, []);
      g.get(r.kind)!.push(r);
    });
    return g;
  }, [filtered]);

  const kindCounts = useMemo(() => {
    const c: Record<string, number> = { all: CORPUS.length };
    CORPUS.forEach((r) => { c[r.kind] = (c[r.kind] || 0) + 1; });
    return c;
  }, [CORPUS]);

  return (
    <div data-testid="pureh-search">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-2">Search</div>
        <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Global Search</h1>
        <div className="mt-1 text-sm text-slate-500">
          Products · Campaigns · Batches · Suppliers · Customers · Invoices · Payments · Creators · plus every page across the platform.
        </div>
      </div>

      <Card testId="search-form">
        <div className="flex items-center gap-3">
          <Search size={16} style={{ color: "var(--ph-ink-muted)" }} />
          <div className="flex-1">
            <TextInput
              value={q}
              onChange={setQ}
              placeholder="Search products, campaigns, batches, invoices…"
              testId="search-input"
              autoFocus
            />
          </div>
          <span className="hidden md:inline-flex items-center gap-1 text-[11px]" style={{ color: "var(--ph-ink-muted)" }}>
            or press <Kbd>⌘</Kbd><Kbd>K</Kbd> anywhere
          </span>
        </div>
        <div className="mt-4 overflow-x-auto">
          <TabBar<ResultKind | "all">
            testId="search-scope-tabs"
            active={scope}
            onChange={setScope}
            tabs={[
              { key: "all", label: "All", count: kindCounts.all },
              ...KIND_ORDER.map((k) => ({
                key: k,
                label: KIND_META[k].label,
                count: kindCounts[k] || 0,
              })),
            ]}
          />
        </div>
      </Card>

      <div className="mt-6">
        {filtered.length === 0 ? (
          <Card testId="search-empty">
            <EmptyState
              title={q ? `No results for “${q}”` : "Start typing"}
              hint="Try a SKU, a batch code, a customer, an invoice number, or a page name."
            />
          </Card>
        ) : (
          <div className="space-y-6">
            {KIND_ORDER.filter((k) => Boolean(grouped.get(k)?.length)).map((k) => {
              const list = grouped.get(k) || [];
              const meta = KIND_META[k];
              const Icon = meta.icon;
              return (
                <Card key={k} padded={false} testId={`search-group-${k}`}>
                  <div className="p-5 border-b" style={{ borderColor: "var(--ph-border-subtle)" }}>
                    <SectionHeader
                      eyebrow={meta.label}
                      title={`${list.length} match${list.length === 1 ? "" : "es"}`}
                      action={<Icon size={14} style={{ color: "var(--ph-ink-muted)" }} />}
                    />
                  </div>
                  <div className="p-2">
                    {list.map((r) => (
                      <ListRow
                        key={r.id}
                        testId={`search-result-${r.id}`}
                        icon={<Icon size={15} />}
                        label={r.title}
                        sublabel={r.subtitle}
                        trailing={<ArrowRight size={12} style={{ color: "var(--ph-ink-soft)" }} />}
                        onClick={() => navigate(r.href)}
                      />
                    ))}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center justify-end text-[11px]" style={{ color: "var(--ph-ink-muted)" }}>
        <span className="inline-flex items-center gap-1">
          <Compass size={11} /> Every entity, everywhere · one operating system.
        </span>
      </div>
    </div>
  );
}
